import axios from "axios";
import AuthenticationRequest from "./requests/AuthenticationRequest";
import AuthenticationResponse from "./responses/AuthenticationResponse";
import { localStorageKeys } from "../utils/localStorageKeys";
import { initializeAppModeForLogin } from "../utils/appMode";

class AuthClient {

  public isLoggedIn(): boolean {
    return localStorage.getItem(localStorageKeys.loginResponse) !== null;
  }

  public getApiRoot() {
    return process.env.REACT_APP_BE_URL;
  }

  public saveAuthenticationResponse(response: AuthenticationResponse): void {
    localStorage.setItem(localStorageKeys.loginResponse, JSON.stringify(response));
  }

  public async authenticate(request: AuthenticationRequest) {
    const response = await axios.post(process.env.REACT_APP_BE_URL + "/account/login", request, {
      withCredentials: true,
    });

    // Persist auth first so listeners read the fresh role/token.
    if (response.status === 200 && response.data?.token) {
      this.saveAuthenticationResponse(response.data as AuthenticationResponse);
      initializeAppModeForLogin(response.data?.userType);
      window.dispatchEvent(new CustomEvent("login"));
    }

    return response;
  }

  public async tryRestoreSession(): Promise<AuthenticationResponse | undefined> {
    if (this.isLoggedIn()) {
      return this.getAuthenticationResponse();
    }

    const response = await fetch(`${this.getApiRoot()}/account/session`, {
      credentials: "include",
    });

    if (!response.ok) {
      return undefined;
    }

    const data = await response.json() as AuthenticationResponse;
    if (!data?.token) {
      return undefined;
    }

    this.saveAuthenticationResponse(data);
    initializeAppModeForLogin(data.userType);
    window.dispatchEvent(new CustomEvent("login"));

    return data;
  }

  public getAuthenticationResponse(): AuthenticationResponse | undefined {
    if (!this.isLoggedIn()) {
      return undefined;
    }
    const authResponse = JSON.parse(
      localStorage.getItem(localStorageKeys.loginResponse) as string
    ) as AuthenticationResponse;

    // City Selector stores runtime city choice in localStorage.
    // Prefer that over login-time city fields.
    const selectedCityRaw = localStorage.getItem("cityInfo");
    if (!selectedCityRaw) {
      return authResponse;
    }

    try {
      const selectedCity = JSON.parse(selectedCityRaw) as {
        cityId?: string;
        cityName?: string;
      };
      return {
        ...authResponse,
        cityId: selectedCity.cityId ?? authResponse.cityId,
        cityName: selectedCity.cityName ?? authResponse.cityName,
      };
    } catch {
      return authResponse;
    }
  }

  public async logout(): Promise<void> {
    const root = this.getApiRoot();
    if (root) {
      try {
        await fetch(`${root}/account/logout`, {
          method: "POST",
          credentials: "include",
        });
      } catch {
        // Still clear client state if the request fails (offline, CORS in dev, etc.)
      }
    }
    localStorage.removeItem(localStorageKeys.loginResponse);
    localStorage.removeItem(localStorageKeys.userImg);
    localStorage.removeItem(localStorageKeys.appMode);
    window.dispatchEvent(new CustomEvent("logout"));
  }
}

export default AuthClient;
